import * as React from 'react';
import { Heading, Select } from '@built/design-system';

import { AuthError, BrandingData } from '../types';
import Notification from '../containers/Notification';
import ActionButton from '../containers/ActionButton';
import PasswordField, { passwordValid } from '../containers/PasswordField';
import * as styles from './SetupPassword.module.scss';
import { getConfig } from '../utils';
import l10n from '@built/i18n';

/**
 * Render UI to set an initial password, or to enable login with an external account
 * if branding is provided and has an external login URL. Also applies custom styles
 * based on passed in branding regardless of whether external login is available.
 */
export default function SetupPassword(props: SetupPasswordProps) {
  const { registering } = props;
  const useMockCognito = getConfig('REACT_APP_USE_MOCK_COGNITO') === 'true';
  return (
    <div className={styles.verticalSpacing}>
      {registering ? renderLeadRegistering(props) : renderLeadMigrating(props)}

      {useMockCognito && (
        <Notification type="warning" title="Mock Cognito Enabled">
          <p>
            {l10n.t(
              'SetupPassword:error.mock.notSupported',
              'Registration is not supported when mock cognito mode is enabled.'
            )}
          </p>
        </Notification>
      )}

      {renderForm(props)}
    </div>
  );
}

export interface SetupPasswordProps {
  /** An error to display to the user */
  authError?: AuthError;

  /** Whether the user needs a new password */
  needsPassword?: boolean;

  /** If the user is registering or migrating */
  registering: boolean;

  /** Is this an IMA user that's migrating? */
  isImaUser?: boolean;

  /** The address of the project */
  address?: string;

  /** The user's username */
  username: string;

  /** The currently entered password value */
  password: string;

  /** The currently selected preferred locale */
  preferredLocale: string;

  /** Optionally indicate that the app is processing */
  busy?: boolean;

  /** Customize the UI with branding data */
  branding?: BrandingData;

  /** Handle the user submission */
  onSubmit(event: React.SyntheticEvent<HTMLFormElement>): void;

  /** Handle updates to the password */
  onChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Handle updates to the locale */
  onChangeLocale(event: React.SyntheticEvent<HTMLSelectElement>): void;
}

function renderLeadMigrating(props: SetupPasswordProps) {
  const { isImaUser } = props;

  if (isImaUser) {
    return (
      <React.Fragment>
        <Heading size="xxl" className="margin-v-sm">
          {l10n.t(
            'SetupPassword:createPassword.heading',
            'Create your new password'
          )}
        </Heading>

        <p className={styles.lead}>
          {l10n.t(
            'SetupPassword:createPassword.message',
            'Create a new password for your account'
          )}
          <strong>{props.username}</strong>.
        </p>
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment>
        <Heading size="xxl" className="margin-v-sm">
          {l10n.t(
            'SetupPassword:securityUpgrade.heading',
            'Account security upgrade required'
          )}
        </Heading>

        <p className={styles.lead}>
          {l10n.t(
            'SetupPassword:securityUpgrade.message',
            `To better protect your account and secure your information, we're
          requiring all users to reset their password using our new security
          standards. To continue, please enter a new password for your account `
          )}{' '}
          <strong>{props.username}</strong>.
        </p>
      </React.Fragment>
    );
  }
}

function renderLeadRegistering(props: SetupPasswordProps) {
  const { username } = props;

  return (
    <React.Fragment>
      <Heading size="xxl" className="margin-v-sm">
        {l10n.t(
          'SetupPassword:lead.heading',
          'We simplify the management of your construction loans.'
        )}
      </Heading>

      <p className={styles.lead}>
        {l10n.t(
          'SetupPassword:lead.invite',
          "You've been invited to collaborate on a project using Built."
        )}
      </p>

      <p className={styles.lead}>
        {l10n.t(
          'SetupPassword:lead.message',
          "Let's get started! To complete the registration process, please create a password for your account "
        )}
        {username && (
          <React.Fragment>
            {' '}
            <strong>{username}</strong>
          </React.Fragment>
        )}
        .
      </p>
    </React.Fragment>
  );
}

function renderForm(props: SetupPasswordProps) {
  const {
    onSubmit,
    authError,
    needsPassword,
    busy,
    onChange,
    onChangeLocale,
    password,
    preferredLocale,
    registering
  } = props;

  return (
    <React.Fragment>
      <form
        className={[styles.form, styles.verticalSpacing].join(' ')}
        onSubmit={onSubmit}
      >
        {authError && (
          <Notification
            title={
              authError.title ||
              l10n.t('SetupPassword:error.auth.heading', 'An error occurred')
            }
            type="danger"
          >
            <p>
              {authError.message ||
                l10n.t(
                  'SetupPassword:error.auth.message',
                  'There was a problem creating your password. Please try again.'
                )}
            </p>
          </Notification>
        )}

        {registering && (
          <Select
            onChange={onChangeLocale}
            label={l10n.t(
              'SetupPassword:preferredLocale.changeLanguage',
              'Change Language'
            )}
            name="preferredLocale"
            size="md"
            value={preferredLocale}
          >
            <option value="en-US">
              {l10n.t('SetupPassword:preferredLocale.english', 'English')}
            </option>
            <option value="fr-CA">
              {l10n.t(
                'SetupPassword:preferredLocale.french',
                'French (Canada)'
              )}
            </option>
          </Select>
        )}

        {needsPassword && (
          <PasswordField
            disabled={busy}
            autoFocus={true}
            onChange={onChange}
            password={password}
            className={styles.passwordField}
          />
        )}

        <ActionButton
          label={l10n.t('SetupPassword:actions.continue', 'Continue')}
          type="submit"
          loading={busy}
          disabled={!passwordValid(props.password)}
        />
      </form>
    </React.Fragment>
  );
}



// WEBPACK FOOTER //
// ./src/components/SetupPassword.tsx